// Chrome refactor Inc 3 — the studio hub dashboard as a dark control room (per
// creator-studio-dark-v1 wireframe), shared by /creator/studio and /admin/studio
// (both render on the video surface). Top row pairs the go-live hero with a
// shows-management panel; then the Live-now / Upcoming row and recent broadcasts
// as cards. Always dark: these
// routes always render the video surface, so video-* tokens are used directly.
//
// Honest placeholders: LiveRoomDetail carries no watcher-count, duration, or view
// count, and there are no VOD thumbnails yet — recent cards show a play-icon
// placeholder + date; those metrics arrive with the recording/VOD + analytics work.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { ArrowRight, CalendarDays, Play } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { GoLiveForm } from '@/features/live/GoLiveForm';
import { deleteRoom, renameRoom } from '@/features/live/roomActions';
import { roomQueries, type LiveRoomDetail } from '@/features/live/roomQueries';
import {
  approveCohostInvite,
  leaveShow,
  listMyCohostInvites,
  listMyCohostedShows,
} from '@/features/live/showActions';
import { useAuth } from '@/lib/auth/useAuth';
import { liveWatchHref } from '@/lib/queries/browse';

function Label({ children }: { children: ReactNode }) {
  return (
    <p className="mb-3 font-sans text-[11px] uppercase tracking-[0.14em] text-video-text-faint">
      {children}
    </p>
  );
}

function RecentCard({ room, hostId }: { room: LiveRoomDetail; hostId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(room.title);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['live-room-recent', hostId] });
  const rename = useMutation({
    mutationFn: () => renameRoom(room.id, title.trim()),
    onSuccess: () => {
      setEditing(false);
      void invalidate();
    },
  });
  const del = useMutation({
    mutationFn: () => deleteRoom(room.id),
    onSuccess: () => void invalidate(),
  });

  return (
    <div className="overflow-hidden rounded-xl border border-video-line bg-video-card">
      <a href={liveWatchHref(room.slug)} className="block">
        <div className="flex aspect-video items-center justify-center border-b border-video-line bg-video-inset">
          <Play className="h-7 w-7 fill-current text-video-text-faint opacity-40" />
        </div>
      </a>
      <div className="space-y-2 p-3">
        {editing ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (title.trim()) rename.mutate();
            }}
            className="space-y-2"
          >
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              autoFocus
              className="w-full rounded-md border border-video-line bg-video-inset px-2 py-1 font-sans text-sm text-video-text outline-none focus:border-video-line-strong"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={!title.trim() || rename.isPending}
                className="rounded-md bg-oxblood-live px-2.5 py-1 font-sans text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
              >
                {rename.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setTitle(room.title);
                  setEditing(false);
                }}
                className="rounded-md border border-video-line px-2.5 py-1 font-sans text-xs text-video-text-muted hover:text-video-text"
              >
                Cancel
              </button>
            </div>
            {rename.isError && (
              <p className="font-sans text-[11px] text-oxblood-live">Could not rename.</p>
            )}
          </form>
        ) : (
          <>
            <a href={liveWatchHref(room.slug)} className="block">
              <p className="line-clamp-2 font-serif text-sm leading-snug text-video-text">
                {room.title}
              </p>
            </a>
            <div className="flex items-center justify-between">
              {room.startedAt ? (
                <span className="font-sans text-[11px] text-video-text-faint">
                  {new Date(room.startedAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              ) : (
                <span />
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="font-sans text-xs text-video-text-muted transition hover:text-video-text"
                >
                  Edit
                </button>
                <button
                  type="button"
                  disabled={del.isPending}
                  onClick={() => {
                    if (window.confirm(`Delete “${room.title}”? This cannot be undone.`))
                      del.mutate();
                  }}
                  className="font-sans text-xs text-video-text-muted transition hover:text-oxblood-live disabled:opacity-50"
                >
                  {del.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
            {del.isError && (
              <p className="font-sans text-[11px] text-oxblood-live">Could not delete.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PendingInvites({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const invites = useQuery({
    queryKey: ['cohost-invites', userId],
    enabled: Boolean(userId),
    queryFn: () => listMyCohostInvites(userId),
  });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['cohost-invites', userId] });
    void qc.invalidateQueries({ queryKey: ['cohosted-shows', userId] });
    void qc.invalidateQueries({ queryKey: ['my-cohost-membership', userId] });
  };
  const approve = useMutation({
    mutationFn: (showId: string) => approveCohostInvite(showId, userId),
    onSuccess: invalidate,
  });
  const decline = useMutation({
    mutationFn: (showId: string) => leaveShow(showId, userId),
    onSuccess: invalidate,
  });
  const items = invites.data ?? [];
  if (items.length === 0) return null;
  const busy = approve.isPending || decline.isPending;
  return (
    <section>
      <Label>Pending cohost invites</Label>
      <ul className="space-y-2">
        {items.map((s) => (
          <li
            key={s.showId}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-video-line bg-video-inset p-4"
          >
            <span className="min-w-0 flex-1 font-serif text-base text-video-text">{s.name}</span>
            <button
              type="button"
              onClick={() => approve.mutate(s.showId)}
              disabled={busy}
              className="rounded-md bg-oxblood-live px-3 py-1.5 font-sans text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
            >
              {approve.isPending ? 'Approving…' : 'Approve'}
            </button>
            <button
              type="button"
              onClick={() => decline.mutate(s.showId)}
              disabled={busy}
              className="font-sans text-xs text-video-text-muted transition hover:text-video-text disabled:opacity-50"
            >
              Decline
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CohostedShows({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const shows = useQuery({
    queryKey: ['cohosted-shows', userId],
    enabled: Boolean(userId),
    queryFn: () => listMyCohostedShows(userId),
  });
  const stop = useMutation({
    mutationFn: (showId: string) => leaveShow(showId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cohosted-shows', userId] });
      void qc.invalidateQueries({ queryKey: ['my-cohost-membership', userId] });
    },
  });
  const items = shows.data ?? [];
  if (items.length === 0) return null;
  return (
    <section>
      <Label>Shows you cohost</Label>
      <ul className="space-y-2">
        {items.map((s) => (
          <li
            key={s.showId}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-video-line bg-video-inset p-4"
          >
            <span className="min-w-0 flex-1 font-serif text-base text-video-text">{s.name}</span>
            <button
              type="button"
              onClick={() => stop.mutate(s.showId)}
              disabled={stop.isPending}
              className="font-sans text-xs text-video-text-muted transition hover:text-oxblood-live disabled:opacity-50"
            >
              {stop.isPending ? 'Leaving…' : 'Stop cohosting'}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function StudioDashboard() {
  const { session } = useAuth();
  const hostId = session?.user.id ?? '';
  // The hub is shared by /creator/studio and /admin/studio. Keep the shows link
  // inside the current chrome so the nav bar doesn't flip between sections.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const showsHref = pathname.startsWith('/admin') ? '/admin/shows' : '/creator/shows';

  const mine = useQuery({ ...roomQueries.mineActive(hostId), enabled: Boolean(hostId) });
  const recent = useQuery({ ...roomQueries.recentEnded(hostId), enabled: Boolean(hostId) });
  const liveRoom = mine.data?.status === 'live' ? mine.data : null;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      {/* Top row: go-live hero + shows-management, two columns aligned with the
          Live-now / Upcoming row below. */}
      <div className="grid gap-6 md:grid-cols-[1.45fr_1fr]">
        {/* Go-live hero */}
        <section className="relative overflow-hidden rounded-xl border border-video-line bg-video-card p-5 md:p-6">
          <span aria-hidden className="absolute inset-x-0 top-0 h-0.5 bg-oxblood-browse" />
          <p className="font-sans text-[11px] uppercase tracking-[0.14em] text-video-text-faint">
            Go live
          </p>
          <h2 className="mb-4 mt-1 font-serif text-2xl text-video-text">Start a broadcast</h2>
          <GoLiveForm hostId={hostId} />
        </section>

        {/* Shows management (moved up from the page footer into the second column) */}
        <Link
          to={showsHref}
          className="group flex flex-col justify-between rounded-xl border border-video-line bg-video-card p-5 transition hover:border-video-line-strong md:p-6"
        >
          <div>
            <p className="font-sans text-[11px] uppercase tracking-[0.14em] text-video-text-faint">
              Manage
            </p>
            <h2 className="mt-1 font-serif text-2xl text-video-text">Shows, team &amp; branding</h2>
          </div>
          <span className="mt-4 inline-flex items-center justify-between gap-3 font-sans text-sm text-video-text-muted">
            Create shows, invite cohosts, set branding
            <ArrowRight className="h-4 w-4 shrink-0 text-silver transition group-hover:translate-x-0.5" />
          </span>
        </Link>
      </div>

      {/* Pending cohost invites (renders only when there are any) */}
      <PendingInvites userId={hostId} />

      {/* Live now + Upcoming */}
      <div className="grid gap-5 md:grid-cols-[1.45fr_1fr]">
        <section>
          <Label>Live now</Label>
          {liveRoom ? (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-video-line-strong bg-video-inset p-4">
              <span className="inline-flex items-center gap-2 rounded-md bg-oxblood-live px-2 py-1 font-sans text-[10.5px] font-semibold uppercase tracking-[0.1em] text-white">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                Live
              </span>
              <span className="min-w-0 flex-1 font-serif text-base leading-tight text-video-text">
                {liveRoom.title}
              </span>
              <Link
                to="/studio/$slug"
                params={{ slug: liveRoom.slug }}
                className="rounded-lg bg-silver px-3 py-1.5 font-sans text-xs font-semibold text-video-bg transition hover:opacity-90"
              >
                Return to studio
              </Link>
              <a
                href={liveWatchHref(liveRoom.slug)}
                target="_blank"
                rel="noreferrer"
                className="font-sans text-xs text-video-text-muted hover:text-video-text"
              >
                Public page ↗
              </a>
            </div>
          ) : (
            <div className="rounded-xl border border-video-line bg-video-inset p-4">
              <p className="font-sans text-sm text-video-text-faint">
                You&apos;re not live right now.
              </p>
            </div>
          )}
        </section>

        <section>
          <Label>Upcoming</Label>
          <div className="rounded-xl border border-video-line bg-video-inset p-4">
            <div className="flex items-center gap-2 font-sans text-sm text-video-text-faint">
              <CalendarDays className="h-4 w-4" />
              Scheduling arrives soon
            </div>
          </div>
        </section>
      </div>

      {/* Recent broadcasts */}
      <section>
        <Label>Recent broadcasts</Label>
        {recent.isLoading ? (
          <p className="font-sans text-sm text-video-text-faint">Loading…</p>
        ) : (recent.data ?? []).length === 0 ? (
          <p className="font-sans text-sm text-video-text-faint">No past broadcasts yet.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {recent.data!.map((room) => (
              <RecentCard key={room.id} room={room} hostId={hostId} />
            ))}
          </div>
        )}
      </section>

      {/* Shows you cohost (renders only when there are any) */}
      <CohostedShows userId={hostId} />
    </div>
  );
}
